import {
    Box,
    Button,
    Center,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerOverlay,
    Flex,
    IconButton,
    Spacer,
    Stack,
    Text,
    VStack,
    useColorModeValue,
    useDisclosure,
} from "@chakra-ui/react";
import moment from "moment/min/moment-with-locales";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    RiCalendarEventFill,
    RiCalendarEventLine,
    RiCloseFill,
    RiCupLine,
    RiDiscordLine,
    RiGamepadFill,
    RiGamepadLine,
    RiGlobalFill,
    RiGlobalLine,
    RiGraduationCapFill,
    RiGraduationCapLine,
    RiHome2Fill,
    RiHome2Line,
    RiInformationFill,
    RiInformationLine,
    RiMenuFill,
    RiRocket2Fill,
    RiRocket2Line,
} from "react-icons/ri";
import { Link } from "react-router";
import { Brand } from "../brand/Brand";
import { Logo } from "../brand/Logo";
import { ColorModeToggle } from "../utils/ColorModeToggle";
import { ContactUs } from "../utils/ContactUs";
import { LanguageToggle } from "../utils/LanguageToggle";
import { SidebarButton, SidebarButtonProps } from "./SidebarButton";

export const SideBar = () => {
    const { t } = useTranslation();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const sidebarContent = useMemo(
        () =>
            [
                (props) => (
                    <SidebarButton
                        key="home"
                        pageName=""
                        iconFill={RiHome2Fill}
                        iconOutline={RiHome2Line}
                        text={t("menuButtons.home")}
                        {...props}
                    />
                ),
                (props) => (
                    <SidebarButton
                        key="games"
                        pageName="games"
                        iconFill={RiGamepadFill}
                        iconOutline={RiGamepadLine}
                        text={t("menuButtons.games")}
                        {...props}
                    />
                ),
                (props) => (
                    <SidebarButton
                        key="events"
                        pageName="events"
                        iconFill={RiCalendarEventFill}
                        iconOutline={RiCalendarEventLine}
                        text={t("menuButtons.events")}
                        {...props}
                    />
                ),
                (props) => (
                    <SidebarButton
                        key="courses"
                        pageName="courses"
                        iconFill={RiGraduationCapFill}
                        iconOutline={RiGraduationCapLine}
                        text={t("menuButtons.courses")}
                        {...props}
                    />
                ),
                (props) => (
                    <SidebarButton
                        key="community"
                        pageName="community"
                        iconFill={RiGlobalFill}
                        iconOutline={RiGlobalLine}
                        text={t("menuButtons.community")}
                        {...props}
                    />
                ),
                (props) => (
                    <SidebarButton
                        key="launcher"
                        pageName="launcher"
                        iconFill={RiRocket2Fill}
                        iconOutline={RiRocket2Line}
                        text="Launcher"
                        {...props}
                    />
                ),
                (props) => (
                    <SidebarButton
                        key="about"
                        pageName="about"
                        iconFill={RiInformationFill}
                        iconOutline={RiInformationLine}
                        text={t("menuButtons.about")}
                        {...props}
                    />
                ),
            ] as ((props: Partial<SidebarButtonProps>) => React.JSX.Element)[],
        [t],
    );

    return (
        <Stack
            direction={{ base: "row", sm: "column" }}
            paddingLeft={{ base: 0, sm: 7 }}
            paddingBottom={{ base: 0, sm: 7 }}
            paddingRight={{ base: 8, sm: 7 }}
            paddingTop={{ base: 0, sm: 7 }}
            w={{ base: "full", sm: "60" }}
            h={{ base: "20", sm: "full" }}
            justifyContent={{ base: "center", sm: "left" }}
            spacing="5"
            fontFamily={"heading"}
            flexGrow={0}
            flexShrink={0}
            borderRightWidth={1}
            borderRightColor={useColorModeValue("gray.200", "gray.700")}
            overflow={"auto"}
            sx={{
                boxShadow: useColorModeValue(
                    "2px 2px 10px rgba(128, 128, 128, 0.2)",
                    "2px 2px 10px rgba(44, 48, 68, 0.5)",
                ),
            }}
        >
            <Box display={{ base: "contents", sm: "none" }}>
                <Flex alignItems="center" justifyContent="center" w="full" px={4}>
                    <IconButton
                        aria-label="Menu"
                        icon={<RiMenuFill fontSize="20px" />}
                        color={useColorModeValue("gray.900", "gray.50")}
                        onClick={onOpen}
                        _hover={{
                            textDecoration: "none",
                        }}
                        bg={"transparent"}
                    />

                    <Spacer />

                    <Link to="/">
                        <Logo w="150px" />
                    </Link>

                    <Spacer />
                </Flex>

                <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
                    <DrawerOverlay />
                    <DrawerContent>
                        <DrawerCloseButton>
                            <RiCloseFill fontSize={"20px"} />
                        </DrawerCloseButton>
                        <DrawerBody>
                            <VStack
                                paddingTop={10}
                                paddingBottom={10}
                                paddingLeft={2}
                                w="full"
                                h="full"
                                alignItems={"left"}
                                spacing="5"
                                fontFamily={"heading"}
                                flexGrow={0}
                                flexShrink={0}
                            >
                                {sidebarContent.map((button) => button({ close: onClose }))}

                                <Spacer />

                                <Button
                                    as="a"
                                    href="https://discord.gg/yhk4REFngH"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="link"
                                    color={useColorModeValue("gray.900", "gray.50")}
                                    justifyContent="flex-start"
                                    _focus={{
                                        color: useColorModeValue("gray.900", "gray.50"),
                                    }}
                                    _hover={{
                                        textDecoration: "none",
                                    }}
                                >
                                    <Center as="span" fontSize="18px" w="20px" mr={2}>
                                        <RiDiscordLine strokeWidth={25} stroke="currentColor" />
                                    </Center>
                                    <Text fontSize="sm" fontWeight={"medium"}>
                                        {t("menuButtons.discord")}
                                    </Text>
                                </Button>

                                <Button
                                    as="a"
                                    href="https://kafe.fi.muni.cz"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="link"
                                    color={useColorModeValue("gray.900", "gray.50")}
                                    justifyContent="flex-start"
                                    _focus={{
                                        color: useColorModeValue("gray.900", "gray.50"),
                                    }}
                                    _hover={{
                                        textDecoration: "none",
                                    }}
                                >
                                    <Center as="span" fontSize="18px" w="20px" mr={2}>
                                        <Brand variant="simple" strokeWeight={25} stroke="currentColor" />
                                    </Center>
                                    <Text fontSize="sm" fontWeight={"medium"}>
                                        {t("menuButtons.kafe")}
                                    </Text>
                                </Button>

                                <ColorModeToggle aria-label="Color mode" />

                                <LanguageToggle aria-label="Language" />

                                <ContactUs />

                                <Text fontSize="xs" color={useColorModeValue("gray.400", "gray.600")}>
                                    {t("menuButtons.madeby")}
                                    <br />© {moment().year()}
                                </Text>
                            </VStack>
                        </DrawerBody>
                    </DrawerContent>
                </Drawer>
            </Box>

            {/* Always rendered; shown only at `sm` and above via CSS. */}
            <Box display={{ base: "none", sm: "contents" }}>
                <Box paddingBottom={4}>
                    <Link to="/">
                        <Logo />
                    </Link>
                </Box>

                {sidebarContent.map((button) => button({}))}

                <Spacer />

                <Button
                    as="a"
                    href="https://discord.gg/yhk4REFngH"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="link"
                    color={useColorModeValue("gray.900", "gray.50")}
                    justifyContent="flex-start"
                    _focus={{
                        color: useColorModeValue("gray.900", "gray.50"),
                    }}
                    _hover={{
                        textDecoration: "none",
                    }}
                >
                    <Center as="span" fontSize="18px" w="20px" mr={2}>
                        <RiDiscordLine />
                    </Center>
                    <Text fontSize="sm" fontWeight={"medium"}>
                        {t("menuButtons.discord")}
                    </Text>
                </Button>

                <Button
                    as="a"
                    href="https://kafe.fi.muni.cz"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="link"
                    color={useColorModeValue("gray.900", "gray.50")}
                    justifyContent="flex-start"
                    _focus={{
                        color: useColorModeValue("gray.900", "gray.50"),
                    }}
                    _hover={{
                        textDecoration: "none",
                    }}
                >
                    <Center as="span" fontSize="18px" w="20px" mr={2}>
                        <RiCupLine />
                    </Center>
                    <Text fontSize="sm" fontWeight={"medium"}>
                        {t("menuButtons.kafe")}
                    </Text>
                </Button>

                <ColorModeToggle aria-label="Color mode" />

                <LanguageToggle aria-label="Language" />

                <ContactUs />

                <Text fontSize="xs" color={useColorModeValue("gray.400", "gray.600")}>
                    {t("menuButtons.madeby")}
                    <br />© {moment().year()}
                </Text>
            </Box>
        </Stack>
    );
};
